import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ARTISTS_FALLBACK, IMG, BAUL_INVITATION } from "@/data";
import { Alpana } from "@/components/Logo";
import { requestTalk, pollTalk } from "@/lib/talk";
import BookingWizard from "@/moncho/BookingWizard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const imgSrc = (u) => (!u || u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);
// ডেটাবেসে ভাষা/ছবি কখনো সাধা-লেখা হয়ে যায় — তবু পর্দা ভাঙবে না
const asList = (v) => (Array.isArray(v) ? v : String(v || "").split(",").map((s) => s.trim()).filter(Boolean));

/* বাউলের ঠোঁটে-আমন্ত্রণ — শো-রিল ভিডিও (ক্যাশ থাকলে তাৎক্ষণিক) */
function useShowreel(artist) {
  const [trailer, setTrailer] = useState(null);
  useEffect(() => {
    if (!artist?.showreel_text) return;
    let alive = true;
    requestTalk(artist.showreel_text, "baul").then((t) => {
      if (!alive || !t) return;
      if (t.video_url) setTrailer({ url: t.video_url });
      else if (t.talk_id)
        pollTalk(t.talk_id).then((url) => {
          if (alive && url) setTrailer({ url });
        });
    });
    return () => {
      alive = false;
    };
  }, [artist?.id]);
  return trailer;
}

function ListenModal({ artist, onClose }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [pos, setPos] = useState(0);
  const [dur, setDur] = useState(0);
  const [failed, setFailed] = useState(false);

  useEffect(
    () => () => {
      try {
        audioRef.current?.pause();
      } catch {}
    },
    [],
  );

  const toggle = async () => {
    const a = audioRef.current;
    if (!a) return;
    try {
      if (playing) {
        a.pause();
        setPlaying(false);
      } else {
        await a.play();
        setPlaying(true);
      }
    } catch {
      setFailed(true);
      setPlaying(false);
    }
  };

  const fmt = (s) => {
    if (!isFinite(s)) return "০:০০";
    return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
  };

  return (
    <motion.div
      className="fixed inset-0 z-[86] flex items-center justify-center bg-dusk/95 p-4 backdrop-blur-md"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      data-testid="listen-modal"
    >
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 16, scale: 0.98 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-lg rounded-2xl border border-gold/25 bg-plaster p-8 text-center shadow-2xl"
      >
        <button data-testid="listen-modal-close" onClick={onClose} className="absolute right-4 top-4 rounded-full p-2 text-sand/50 transition-colors hover:bg-sand/10 hover:text-sand" aria-label="বন্ধ করুন">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>

        <Alpana className="mx-auto w-24" />
        <p className="mt-5 text-[11px] tracking-[0.25em] text-gold/70">শিল্পীর সুর • প্রকৃত রেকর্ডিং</p>
        <h3 className="mt-2 font-display text-2xl text-sand">{artist.name}</h3>
        <p className="mt-1 text-sm text-sand/55">{artist.art_form} — তাঁর ঘরানার গান</p>

        <div className="mt-6 flex items-end justify-center gap-1.5">
          {[0, 1, 2, 3, 4, 5, 6].map((i) => (
            <span
              key={i}
              className="animate-eq h-10 w-1.5 rounded-full bg-gold/80"
              style={{
                animationDelay: `${i * 0.09}s`,
                animationDuration: `${0.65 + (i % 3) * 0.14}s`,
                animationPlayState: playing ? "running" : "paused",
                opacity: playing ? 1 : 0.35,
              }}
            />
          ))}
        </div>

        <button
          data-testid="listen-play-button"
          onClick={toggle}
          className="mt-5 flex h-16 w-16 items-center justify-center rounded-full bg-vermilion text-alpana shadow-[0_12px_36px_rgba(200,62,43,0.45)] transition-transform duration-300 hover:scale-105"
          aria-label={playing ? "থামান" : "শুনুন"}
        >
          {playing ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M7 5h4v14H7zM13 5h4v14h-4z" />
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5.5v13l11-6.5-11-6.5Z" />
            </svg>
          )}
        </button>

        <div className="mt-5 flex items-center gap-3">
          <span className="w-10 text-[11px] text-sand/50">{fmt(pos)}</span>
          <input
            type="range"
            data-testid="listen-seek"
            min="0"
            max={dur || 100}
            value={pos}
            onChange={(e) => {
              const v = Number(e.target.value);
              if (audioRef.current) audioRef.current.currentTime = v;
              setPos(v);
            }}
            className="h-1 flex-1 accent-[#E69A3B]"
            aria-label="গানের অংশ"
          />
          <span className="w-10 text-[11px] text-sand/50">{fmt(dur)}</span>
        </div>

        <p className="mt-4 text-[11px] leading-relaxed text-sand/45">
          {failed ? "গানটা এই মুহূর্তে আসছে না — লাইন একটু দুর্বল, আবার চাপুন।" : artist.audio_note}
        </p>
        <audio ref={audioRef} src={artist.audio} preload="none" onTimeUpdate={(e) => setPos(e.target.currentTime)} onLoadedMetadata={(e) => setDur(e.target.duration)} onEnded={() => setPlaying(false)} />
      </motion.div>
    </motion.div>
  );
}

function WatchModal({ artist, trailer, onClose }) {
  // শিল্পীর নিজের আপলোড-করা ভিডিও — থাকলে সেটাই প্রধান, নইলে ঠোঁটে-আমন্ত্রণ, নইলে ছবি
  const vids = (Array.isArray(artist.videos) ? artist.videos : []).filter((v) => v && v.url);
  const [pick, setPick] = useState(0);
  const chosen = vids[pick];
  const cap = chosen ? "শিল্পীর নিজের পরিবেশনা" : trailer?.url ? "শিল্পীর নিজের আমন্ত্রণ" : "পরিবেশনার ঝলক";
  return (
    <motion.div
      className="fixed inset-0 z-[86] flex items-center justify-center bg-dusk/95 p-4 backdrop-blur-md sm:p-10"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      data-testid="watch-modal"
    >
      <button
        data-testid="watch-modal-close"
        onClick={onClose}
        className="absolute right-5 top-5 z-10 rounded-full border border-sand/25 bg-dusk/60 p-2.5 text-sand/80 backdrop-blur-md transition-colors hover:text-gold"
        aria-label="বন্ধ করুন"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <path d="M18 6 6 18M6 6l12 12" />
        </svg>
      </button>
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.97 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-4xl overflow-hidden rounded-2xl border border-gold/25 shadow-2xl"
      >
        {chosen ? (
          <video
            key={chosen.url}
            src={imgSrc(chosen.url)}
            controls
            autoPlay
            playsInline
            data-testid="performance-video"
            className="h-[55vh] w-full bg-black object-contain"
          />
        ) : trailer?.url ? (
          <video src={trailer.url} autoPlay loop muted playsInline data-testid="showreel-video" className="h-[55vh] w-full object-cover" />
        ) : (
          <motion.img
            src={imgSrc(artist.profile_img)}
            alt={artist.name}
            animate={{ scale: [1, 1.07] }}
            transition={{ repeat: Infinity, repeatType: "reverse", duration: 16, ease: "easeInOut" }}
            className="h-[55vh] w-full object-cover"
          />
        )}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-dusk/85 via-transparent to-dusk/30" />
        {vids.length > 1 && (
          <div className="absolute inset-x-0 top-0 z-10 flex gap-2 overflow-x-auto p-3" data-testid="performance-video-list">
            {vids.map((v, i) => (
              <button
                key={v.id || v.url}
                data-testid={`performance-video-chip-${v.id || i}`}
                onClick={() => setPick(i)}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-xs backdrop-blur-md transition-colors ${
                  i === pick ? "bg-gold text-dusk" : "border border-sand/25 bg-dusk/60 text-sand/80 hover:text-gold"
                }`}
              >
                {v.title}
              </button>
            ))}
          </div>
        )}
        <div className="absolute inset-x-0 bottom-0 flex items-end justify-between p-5">
          <div>
            <p className="flex items-center gap-2 text-xs tracking-wide text-gold">
              <span className="h-2 w-2 animate-flicker rounded-full bg-vermilion" />
              {cap}
            </p>
            <p className="mt-1 font-display text-lg text-alpana">{artist.name}{chosen ? ` — ${chosen.title}` : ""}</p>
          </div>
          <p className="hidden font-latin text-[11px] italic text-sand/60 sm:block">প্রোটোটাইপ • পূর্ণ শো-রিল শীঘ্রই</p>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* শিল্পী-কার্ড — বড় ভিজ্যুয়াল, হাতে-বোনা অনুভূতি */
function ArtistCard({ artist, flip, onListen, onWatch, onStory, onBook }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 44 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className="group grid cursor-pointer overflow-hidden rounded-2xl border border-gold/20 bg-earthen/70 md:grid-cols-2"
      data-testid={`artist-card-${artist.id}`}
      onClick={onStory}
    >
      <div className={`relative h-[320px] overflow-hidden md:h-[440px] ${flip ? "md:order-2" : ""}`}>
        <img src={imgSrc(artist.img)} alt={artist.name} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.05]" />
        <div className="absolute inset-0 bg-gradient-to-t from-dusk/40 via-transparent to-transparent" />
        <span className="absolute left-5 top-5 flex items-center gap-2 rounded-full border border-gold/35 bg-dusk/70 px-4 py-1.5 text-[11px] text-gold backdrop-blur-md">
          <span className="h-1.5 w-1.5 animate-flicker rounded-full bg-vermilion" />
          পরিবেশনার জন্য হাতে-হাতে
        </span>
      </div>
      <div className="flex flex-col justify-center p-7 sm:p-10">
        <p className="font-latin text-xs italic tracking-[0.25em] text-gold/85">{artist.art_form}</p>
        <h3 className="mt-3 font-display text-4xl text-alpana sm:text-5xl">{artist.name}</h3>
        <p className="mt-3 text-sm leading-relaxed text-sand/75">
          {artist.location} • {asList(artist.languages).join(", ")} • {artist.price_display}
        </p>
        <p className="mt-3 line-clamp-2 text-sm leading-relaxed text-sand/60">{artist.story}</p>
        <div className="mt-6 flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
          {artist.audio && (
            <button
              data-testid={`artist-action-listen-${artist.id}`}
              onClick={onListen}
              className="rounded-full border border-gold/40 bg-dusk/40 px-4 py-2 text-sm text-gold backdrop-blur-md transition-all duration-300 hover:bg-gold hover:text-dusk"
            >
              শুনুন
            </button>
          )}
          <button
            data-testid={`artist-action-watch-${artist.id}`}
            onClick={onWatch}
            className="rounded-full border border-gold/40 bg-dusk/40 px-4 py-2 text-sm text-gold backdrop-blur-md transition-all duration-300 hover:bg-gold hover:text-dusk"
          >
            দেখুন
          </button>
          <button
            data-testid={`artist-action-story-${artist.id}`}
            onClick={onStory}
            className="rounded-full border border-alpana/35 bg-dusk/40 px-4 py-2 text-sm text-alpana backdrop-blur-md transition-all duration-300 hover:bg-alpana hover:text-dusk"
          >
            শিল্পীর গল্প
          </button>
          <button
            data-testid={`artist-action-book-${artist.id}`}
            onClick={onBook}
            className="rounded-full bg-vermilion px-4 py-2 text-sm text-alpana shadow-[0_10px_30px_rgba(200,62,43,0.35)] transition-all duration-300 hover:bg-[#d84a35]"
          >
            আমার অনুষ্ঠানে বুক করুন
          </button>
        </div>
      </div>
    </motion.article>
  );
}

/* শিল্পীর ঠিকানা — প্রোফাইল */
function ArtistSpace({ artist, slots, trailer, onListen, onWatch, onBook, onBack }) {
  const upcoming = slots.filter((s) => s.performer === artist.performer_match);
  return (
    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} data-testid="artist-space">
      <button
        data-testid="artist-space-back"
        onClick={onBack}
        className="mb-6 flex items-center gap-2 rounded-full border border-sand/25 bg-earthen/60 px-4 py-1.5 text-xs text-sand/85 transition-colors hover:border-gold/60 hover:text-gold"
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
        শিল্পী-পাড়ায় ফিরুন
      </button>

      <div className="relative overflow-hidden rounded-2xl border border-gold/20">
        <img src={imgSrc(artist.profile_img)} alt={artist.name} className="h-[46vh] min-h-[300px] w-full object-cover" data-testid="artist-space-visual" />
        <div className="absolute inset-0 bg-gradient-to-t from-dusk via-dusk/25 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 p-6 sm:p-9">
          <p className="font-latin text-xs italic tracking-[0.25em] text-gold/85">{artist.art_form}</p>
          <h2 className="mt-2 font-display text-4xl text-alpana sm:text-5xl">{artist.name}</h2>
          <p className="mt-2 text-sm text-sand/80">
            {artist.location} • ভাষা: {asList(artist.languages).join(", ")}
          </p>
        </div>
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-5">
        <div className="lg:col-span-3">
          <h3 className="font-display text-2xl text-sand" data-testid="artist-story-title">শিল্পীর গল্প</h3>
          <p className="mt-3 text-sm leading-loose text-sand/75" data-testid="artist-story">{artist.story}</p>

          <div className="mt-6 flex flex-wrap gap-3">
            {artist.audio && (
              <button data-testid={`space-listen-${artist.id}`} onClick={onListen} className="flex items-center gap-2 rounded-full border border-gold/40 px-5 py-2.5 text-sm text-gold transition-all duration-300 hover:bg-gold hover:text-dusk">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 14v-2a9 9 0 0 1 18 0v2" />
                  <rect x="3" y="14" width="4" height="6" rx="1.5" />
                  <rect x="17" y="14" width="4" height="6" rx="1.5" />
                </svg>
                গান শুনুন
              </button>
            )}
            <button data-testid={`space-watch-${artist.id}`} onClick={onWatch} className="flex items-center gap-2 rounded-full border border-gold/40 px-5 py-2.5 text-sm text-gold transition-all duration-300 hover:bg-gold hover:text-dusk">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5.5v13l11-6.5-11-6.5Z" />
              </svg>
              পরিবেশনা দেখুন
            </button>
          </div>

          <h3 className="mt-10 font-display text-2xl text-sand">ছবি-ঝলক</h3>
          <div className="mt-4 flex gap-3 overflow-x-auto pb-2" data-testid="artist-photos">
            {asList(artist.photos).map((p, i) => (
              <img key={i} src={imgSrc(p)} alt="শিল্পীর ঝলক" loading="lazy" className="h-28 w-44 shrink-0 rounded-xl border border-gold/15 object-cover" />
            ))}
          </div>

          <h3 className="mt-10 font-display text-2xl text-sand">আসন্ন মুক্ত পরিবেশনা</h3>
          {upcoming.length ? (
            <div className="mt-4 space-y-3" data-testid="artist-upcoming">
              {upcoming.map((s) => (
                <div key={s.id} className="flex items-center justify-between rounded-xl border border-gold/15 bg-earthen/70 px-5 py-3.5">
                  <div>
                    <p className="text-sm text-sand">{s.form}</p>
                    <p className="mt-0.5 text-xs text-sand/55">{s.day} • {s.time} • বটতলা প্রাঙ্গণ</p>
                  </div>
                  <span className="rounded-full border border-gold/30 px-3 py-1 text-[10px] text-gold">দেখা যাবে</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-3 text-sm text-sand/55">এই মুহূর্তে মুক্ত পরিবেশনার খবর নেই — তাঁকে সরাসরি আপনার অনুষ্ঠানে ডাকুন।</p>
          )}
        </div>

        <aside className="h-max rounded-2xl border border-gold/20 bg-earthen/70 p-6 lg:col-span-2" data-testid="artist-booking-info">
          <p className="text-[11px] tracking-[0.25em] text-gold/70">বুকিং-তথ্য</p>
          <dl className="mt-4 space-y-3 text-sm">
            <div className="flex justify-between gap-3">
              <dt className="text-sand/50">শুরুর দাম</dt>
              <dd className="text-right text-sand" data-testid="artist-price-start">{artist.price_display}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-sand/50">ধারা</dt>
              <dd className="text-right text-sand">{artist.price_range}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-sand/50">নোটিশ</dt>
              <dd className="text-right text-sand">{artist.notice_days} দিন</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-sand/50">যাবেন যেখানে</dt>
              <dd className="text-right text-sand">আপনার বাড়ি, উঠান, স্কুল, প্রাঙ্গণ</dd>
            </div>
          </dl>
          <p className="mt-4 rounded-xl border border-gold/15 bg-dusk/50 p-3 text-[11px] leading-relaxed text-sand/60">
            শিল্পী আসবেন আপনার অনুষ্ঠানে — তারিখ দিন, হাজিরা মিলবে, দর-কথা শিল্পীর সঙ্গেই। পেমেন্ট পরে, হাতে-হাতে।
          </p>
          <button
            data-testid={`artist-book-cta-${artist.id}`}
            onClick={onBook}
            className="mt-5 w-full rounded-full bg-vermilion py-3.5 text-sm font-medium text-alpana shadow-[0_10px_30px_rgba(200,62,43,0.35)] transition-all duration-300 hover:bg-[#d84a35]"
          >
            আমার অনুষ্ঠানে বুক করুন
          </button>
        </aside>
      </div>
    </motion.div>
  );
}

export default function Moncho({ navTo }) {
  const [artists, setArtists] = useState(ARTISTS_FALLBACK);
  const [slots, setSlots] = useState([]);
  const [space, setSpace] = useState(null); // শিল্পীর ঠিকানায় ঢুকেছেন
  const [modal, setModal] = useState(null); // {kind, artist}
  const [wizard, setWizard] = useState(null); // artist
  const artistsRef = useRef(artists);
  artistsRef.current = artists;
  const spaceRef = useRef(space);
  spaceRef.current = space;

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/moncho/artists`);
        const d = await r.json();
        if (d.artists?.length) setArtists(d.artists);
      } catch {}
      try {
        const r = await fetch(`${API}/moncho/schedule`);
        const d = await r.json();
        if (d.slots?.length) setSlots(d.slots);
      } catch {}
    })();
  }, []);

  // সুচরিতার দুই-উত্তর — শুনতে চাই / বুক করতে চাই
  useEffect(() => {
    const onAction = (e) => {
      const action = e.detail?.action;
      const list = artistsRef.current;
      const baul = list.find((a) => a.audio) || list[0];
      if (action === "listen") {
        setSpace(null);
        if (baul) setModal({ kind: "listen", artist: baul });
        setTimeout(() => document.querySelector('[data-testid="artist-street"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 500);
      }
      if (action === "book") {
        setSpace(null);
        if (baul) setWizard(baul);
      }
    };
    window.addEventListener("rom-host-action", onAction);
    return () => window.removeEventListener("rom-host-action", onAction);
  }, []);

  const trailer = useShowreel(modal?.kind === "watch" ? modal.artist : null);
  const featured = artists[0];

  return (
    <div>
      {/* ---------- উঠান — বটগাছের নিচে বাউল ---------- */}
      <section className="relative flex min-h-[92svh] items-end overflow-hidden">
        <motion.img
          src={imgSrc("/api/media/moncho/courtyard.jpg")}
          alt="রং ও মঞ্চ — শিল্পীদের উঠান"
          data-testid="moncho-hero-image"
          initial={{ scale: 1.07 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.6, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-dusk via-dusk/15 to-dusk/40" />
        <div className="night-glow" />
        <div className="relative z-10 mx-auto w-full max-w-[1400px] px-6 pb-44 sm:px-10 sm:pb-36">
          <motion.button
            data-testid="back-to-village-moncho"
            onClick={() => navTo("gate", "গ্রাম-প্রবেশ")}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.6 }}
            className="mb-7 flex items-center gap-2 rounded-full border border-sand/25 bg-dusk/40 px-4 py-1.5 text-xs text-sand/85 backdrop-blur-md transition-colors hover:border-gold/60 hover:text-gold"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            গ্রামের গেটে ফিরুন
          </motion.button>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.35, duration: 0.7 }} className="font-latin text-xs italic tracking-[0.3em] text-gold/85 drop-shadow-[0_1px_6px_rgba(0,0,0,0.9)]">
            THE FOLK STAGE — ARTISTS FOR YOUR EVENT
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45, duration: 0.9, ease: [0.22, 1, 0.36, 1] }} className="mt-3 font-display text-6xl text-alpana sm:text-7xl">
            রং ও মঞ্চ
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.65, duration: 0.7 }} className="mt-3 max-w-xl text-base text-sand/85">
            শিল্পীদের নিজের উঠানে আপনি এসেছেন — গান শুনুন, গল্প জানুন, আর শিল্পীকে ডেকে আনুন আপনার অনুষ্ঠানে।
          </motion.p>
        </div>
      </section>

      <section className="mx-auto max-w-[1300px] px-6 py-14 sm:px-10 sm:py-20">
        {/* ---------- শিল্পী-পাড়া ---------- */}
        {space ? (
          <ArtistSpace
            artist={space}
            slots={slots}
            trailer={null}
            onListen={() => setModal({ kind: "listen", artist: space })}
            onWatch={() => setModal({ kind: "watch", artist: space })}
            onBook={() => setWizard(space)}
            onBack={() => setSpace(null)}
          />
        ) : (
          <div data-testid="artist-street">
            <div className="mb-10 flex flex-col items-center text-center">
              <Alpana className="w-28" />
              <h2 className="mt-5 font-display text-3xl text-sand sm:text-4xl">শিল্পী-পাড়া</h2>
              <p className="mt-2 font-latin text-sm italic text-gold/70">কার ঘরের উঠানে পা রাখবেন?</p>
            </div>
            <div className="space-y-8">
              {artists.map((a, i) => (
                <ArtistCard
                  key={a.id}
                  artist={a}
                  flip={i % 2 === 1}
                  onListen={() => a.audio && setModal({ kind: "listen", artist: a })}
                  onWatch={() => setModal({ kind: "watch", artist: a })}
                  onStory={() => {
                    setSpace(a);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  onBook={() => setWizard(a)}
                />
              ))}
            </div>
          </div>
        )}
      </section>

      <AnimatePresence>
        {modal?.kind === "listen" && <ListenModal key="listen" artist={modal.artist} onClose={() => setModal(null)} />}
        {modal?.kind === "watch" && <WatchModal key="watch" artist={modal.artist} trailer={trailer} onClose={() => setModal(null)} />}
        {wizard && (
          <BookingWizard
            key={"wizard" + wizard.id}
            artist={wizard}
            onClose={() => setWizard(null)}
            onHostLine={(text) => window.dispatchEvent(new CustomEvent("rom-host-say", { detail: { text } }))}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
